import _ from 'underscore';
import React, { useMemo, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import {
  Body1,
  Body2,
  Button,
  Popover,
  PopoverTrigger,
  PopoverSurface,
  useId,
  Link,
} from '@fluentui/react-components';

import { Status } from '../const';
import { StatusIcon } from './status-icon';
import { useStyles } from '../styles/status-popover-styles';

export function StatusPopoverItem({
  name,
  status,
  statusLabelMsg,
  cardMsg,
  cardButtonText,
  onCardButtonClicked,
  customPopoverProps,
  learnMoreLink,
  i18n,
}) {
  const classes = useStyles();
  const id = useId();
  const triggerRef = useRef(null);

  // TODO: This is a workaround to focus on the trigger element when the popover is opened.
  // We should remove this workaround once the issue is fixed in the Fluent UI.
  const setFocus = useCallback(() => {
    if (triggerRef && triggerRef.current) {
      triggerRef.current.focus();
    }
  }, [triggerRef]);

  const labelMsgPart = useMemo(() => {
    if (_.isEmpty(statusLabelMsg)) {
      return null;
    }

    if (cardMsg) {
      return (
        <Popover trapFocus withArrow openOnHover {...customPopoverProps}>
          <PopoverTrigger>
            <Body2 underline className={classNames('popover-trigger', 'status-label-message', classes.statusLabelMsgWithPopover)} ref={triggerRef} onMouseOver={setFocus}>{statusLabelMsg}</Body2>
          </PopoverTrigger>
          <PopoverSurface aria-labelledby={id}>
            <div className={classNames(classes.popover)}>
              <div id={id} className={classNames('ba-popover-title', classes.popoverTitle)}>
                <Body2 className={classNames(classes.popoverTitleText)}>{`${name}: ${statusLabelMsg}`}</Body2>
              </div>
              <Body1 block className={classNames('ba-popover-text', classes.popoverMsg)}>{cardMsg}</Body1>
              <span className={classes.footer}>
                {
                  onCardButtonClicked &&
                  <Button
                    onClick={onCardButtonClicked}
                    className={classNames('ba-popover-button', classes.popoverButton)}
                    autoFocus
                  >
                    {cardButtonText}
                  </Button>
                }
                {learnMoreLink &&
                  <Link
                    href={learnMoreLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classNames('ba-popover-learn-more', classes.learnMore)}
                  >
                    {i18n.getString(_TL_('Learn more'))}
                  </Link>
                }
              </span>
            </div>
          </PopoverSurface>
        </Popover>
      );
    }

    return (
      <Body2 className={classNames('status-label-message', classes.statusLabelMsg)}>{statusLabelMsg}</Body2>
    );
  }, [cardMsg, classes.statusLabelMsg, classes.statusLabelMsgWithPopover, classes.popover, classes.popoverTitle, classes.popoverTitleName, classes.popoverTitleValue, classes.popoverMsg, classes.popoverButton, customPopoverProps, statusLabelMsg, setFocus, id, name, onCardButtonClicked, cardButtonText]);

  const className = name && name.replaceAll(' ', '-').toLowerCase();
  return (
    <div className={classNames('card-status-item', status, className, classes.statusItem)}>
      <StatusIcon status={status} />
      <Body2 className={classNames(classes.nameText)}>{`${name}:`}</Body2>
      {labelMsgPart}
    </div>
  );
}

StatusPopoverItem.propTypes = {
  name: PropTypes.string.isRequired,
  status: PropTypes.oneOf(Object.values(Status)).isRequired,
  statusLabelMsg: PropTypes.string.isRequired,
  cardMsg: PropTypes.string,
  onCardButtonClicked: PropTypes.func,
  cardButtonText: PropTypes.string,
  customPopoverProps: PropTypes.object,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  learnMoreLink: PropTypes.string,
};

StatusPopoverItem.defaultProps = {
  cardMsg: '',
  onCardButtonClicked: null,
  cardButtonText: '',
  customPopoverProps: {},
  learnMoreLink: undefined,
};
